import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "../../../utils/toast";
import { createCardView, getOpportunityCard } from "../../../services/api";
import {
  CardViewRequestDto,
  MarketplaceCardResponseDto,
  TemplateField,
} from "../../../types/types";
import { getCurrencySymbol } from "../../../utils/opportunities/opportunityAndReadyRoster";
import { RoleBasedActions } from "./roleBasedActions";
import {
  FinancialComponentList,
  PayoutPolicyList,
} from "./opportunityViewFinancials";
import {
  EMPTY_VALUE,
  FinancialComponent,
  JsonObject,
  JsonValue,
  OpportunityFormData,
  flattenTranslationValues,
  formatAmount,
  formatChoiceText,
  formatDisplayDate,
  formatSportLabel,
  formatWeightClassLabel,
  getFieldOptionLabel,
  getStepFields,
  isBlank,
  safe,
  isFileValue,
  isIsoDateString,
  shouldFormatChoiceValue,
  shouldRenderNestedValue,
  toTitleCase,
  ViewStep,
} from "./opportunityViewHelpers";
import {
  CARD_SHADOW_CLASS,
  ContractProgress,
  FileValue,
  InfoPair,
  ManagerPanel,
  Section,
} from "./opportunityViewParts";

interface OpportunityViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardId: string | number | null;
  activeTab: "all" | "myCards";
  onRefresh?: () => void | Promise<void>;
}

type LoadStatus = "loading" | "ready" | "error";

interface DisplayField {
  key: string;
  label: string;
  value: JsonValue;
  field: TemplateField;
}

interface StepDisplay {
  narrow: DisplayField[];
  wide: DisplayField[];
  groups: DisplayField[];
}

const VIEW_TRACKING_THROTTLE_MS = 5 * 60 * 1000;
const COMPONENTS_STEP_ID = "components";
const NOTES_STEP_ID = "notes";
const MONETARY_KEY_PATTERN = /amount|purse/i;
const ID_FIELDS_WITH_NAME_SIBLING = new Set(["opponentFighterId"]);
const HIDDEN_UNLESS_TRUE_FIELDS = new Set(["isTitleFight"]);
const STATIC_LABEL_STEPS = new Set(["core", "details"]);

const FIELD_ORDER_BY_STEP: Record<string, string[]> = {
  core: ["opponentFighterName", "paymentMode", "currency", "baseAmount"],
  details: [
    "sport",
    "weightClass",
    "rounds",
    "roundDuration",
    "ruleset",
    "eventDate",
    "isTitleFight",
    "titleDescription",
    "medicalSupportProvided",
    "medicalDocument",
  ],
};

const toKeySlug = (title: string): string =>
  String(title || "")
    .replace(/[^A-Za-z0-9\s]/g, "")
    .trim()
    .split(/\s+/)
    .map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join("");

const isFieldVisible = (
  field: TemplateField,
  data: JsonObject,
): boolean => {
  if (!field.visibleWhen) return true;

  return Object.entries(field.visibleWhen).every(([key, expected]) =>
    key === "mode" ? expected === "view" : data?.[key] === expected,
  );
};

const isWideField = (field: TemplateField, value: unknown): boolean =>
  field.type === "textarea" ||
  (field.type as string) === "file_upload" ||
  isFileValue(value);

const trackCardView = async (viewedCardId: string | number) => {
  try {
    const storageKey = `lastViewed_${viewedCardId}`;
    const lastViewed = localStorage.getItem(storageKey);
    if (lastViewed && Date.now() - Number(lastViewed) < VIEW_TRACKING_THROTTLE_MS) {
      return;
    }

    const geoResponse = await fetch("https://ipapi.co/json");
    const geoData = await geoResponse.json();
    const userJson = localStorage.getItem("loginResponse");
    const user = userJson ? JSON.parse(userJson) : null;
    if (!user?.username) return;

    const dto: CardViewRequestDto = {
      marketplaceCardId: viewedCardId,
      informationRequestDto: {
        ipAddress: geoData.ip,
        countryCode: geoData.country_code,
        city: geoData.city,
        browserInfo: navigator.userAgent,
        sourcePage: window.location.pathname,
      },
    };

    await createCardView(user.username, dto);
    localStorage.setItem(storageKey, Date.now().toString());
  } catch (err) {
    console.error("Geo tracking failed", err);
  }
};

export const OpportunityViewModal: React.FC<OpportunityViewModalProps> = ({
  isOpen,
  onClose,
  cardId,
  activeTab,
  onRefresh,
}) => {
  const [viewCard, setViewCard] = useState<MarketplaceCardResponseDto | null>(null);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const { t, i18n } = useTranslation("opportunities");
  const navigate = useNavigate();
  const translateRef = useRef(t);
  translateRef.current = t;

  const reverseTranslationMap = useMemo(() => {
    const enBundle = i18n.getResourceBundle("en", "opportunities") || {};
    const marketBundle = i18n.getResourceBundle("en", "marketPlaceCards") || {};

    return {
      ...flattenTranslationValues(enBundle),
      ...flattenTranslationValues(marketBundle),
    };
  }, [i18n]);

  useEffect(() => {
    if (!isOpen || !cardId) {
      setViewCard(null);
      setStatus(isOpen ? "error" : "loading");
      return;
    }

    let cancelled = false;

    const loadCard = async () => {
      setStatus("loading");
      try {
        const response = await getOpportunityCard(cardId);
        if (cancelled) return;
        setViewCard(response?.data ? response.data : response);
        setStatus("ready");
      } catch {
        if (cancelled) return;
        setStatus("error");
        toast.error(translateRef.current("failed_to_load_opportunity_details"));
      }
    };

    loadCard();
    trackCardView(cardId);

    return () => {
      cancelled = true;
    };
  }, [isOpen, cardId]);

  const tr = (text?: string, fallback = ""): string => {
    if (!text) return fallback;

    const direct = t(text);
    if (direct !== text) return direct;

    const mappedKey = reverseTranslationMap[text];
    if (mappedKey) {
      const translated = t(mappedKey);
      if (translated !== mappedKey) return translated;

      const oppMapped = i18n.t(mappedKey, { ns: "opportunities" });
      if (oppMapped !== mappedKey) return oppMapped;

      const marketMapped = i18n.t(mappedKey, { ns: "marketPlaceCards" });
      if (marketMapped !== mappedKey) return marketMapped;
    }

    return text;
  };

  const opportunity = viewCard?.opportunityCardResponse;
  const formData: OpportunityFormData = opportunity?.formData || {};
  const template = opportunity?.templateJson || null;
  const currencySymbol = getCurrencySymbol(String(formData.core?.currency || ""));
  const baseAmount = Number(formData.core?.baseAmount) || 0;
  const steps: ViewStep[] = Array.isArray(template?.steps)
    ? template.steps.filter((step: ViewStep) => step.visible !== false)
    : [];

  const getFieldLabel = (key: string, fallback: string): string =>
    t(`opportunity_view.fields.${key}`, { defaultValue: tr(fallback) });

  const getSectionTitle = (title: string): string =>
    t(`opportunity_view.sections.${toKeySlug(title)}`, {
      defaultValue: tr(title),
    });

  const getTemplateField = (fieldPath: string): TemplateField | undefined => {
    const [stepId, ...fieldParts] = fieldPath.split(".");
    if (!stepId || fieldParts.length === 0) return undefined;

    const step = steps.find((item) => item.stepId === stepId);
    if (!step) return undefined;

    let fields = getStepFields(step);
    let field: TemplateField | undefined;

    for (const fieldKey of fieldParts) {
      field = fields.find((item) => item.key === fieldKey);
      fields = field && Array.isArray(field.fields) ? field.fields : [];
    }

    return field;
  };

  const getOptionLabel = (fieldPath: string, value: string): string =>
    getFieldOptionLabel(getTemplateField(fieldPath), value, t) ||
    formatChoiceText(value, t);

  const handleProfileNavigation = (profileId?: string | number | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };

  const formatValue = (value: JsonValue, key?: string): React.ReactNode => {
    if (isBlank(value)) return EMPTY_VALUE;

    if (isFileValue(value)) {
      return <FileValue label={key ? toTitleCase(key) : t("document")} value={value} />;
    }

    if (typeof value === "boolean") {
      return value ? t("yes") : t("no");
    }

    if (typeof value === "number") {
      const isMonetary =
        key === "value" || (key ? MONETARY_KEY_PATTERN.test(key) : false);
      return (
        <span className="tabular-nums">
          {isMonetary ? formatAmount(value, currencySymbol) : value}
        </span>
      );
    }

    if (typeof value === "string") {
      if (isIsoDateString(value)) return formatDisplayDate(value);
      if (key === "currency") return value.toUpperCase();
      if (key === "sport") return formatSportLabel(value);
      if (key === "weightClass") return formatWeightClassLabel(value);
      if (shouldFormatChoiceValue(value, key)) {
        return formatChoiceText(value, t);
      }
      return value;
    }

    return String(value);
  };

  const formatFieldValue = (
    field: TemplateField | undefined,
    value: JsonValue,
  ): React.ReactNode => {
    if (Array.isArray(value) && field?.options?.length) {
      if (!value.length) return EMPTY_VALUE;
      return value
        .map(
          (item) =>
            getFieldOptionLabel(field, item, t) ||
            formatChoiceText(String(item), t),
        )
        .join(", ");
    }

    return getFieldOptionLabel(field, value, t) || formatValue(value, field?.key);
  };

  const renderNestedValue = (value: JsonValue): React.ReactNode => {
    if (isBlank(value)) return <span className="text-gray-400">{EMPTY_VALUE}</span>;
    if (isFileValue(value)) return <FileValue label={t("document")} value={value} />;

    if (Array.isArray(value)) {
      if (!value.length) return <span className="text-gray-400">{EMPTY_VALUE}</span>;
      return (
        <div className="space-y-0.5">
          {value.map((item, index) => (
            <div key={index} className="text-xs text-gray-700">
              {typeof item === "object" ? renderNestedValue(item) : formatValue(item)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === "object") {
      const entries = Object.entries(value).filter(([, item]) => !isBlank(item));
      if (!entries.length) return <span className="text-gray-400">{EMPTY_VALUE}</span>;
      return (
        <div className="space-y-0.5">
          {entries.map(([key, item]) => (
            <div key={key} className="flex flex-wrap gap-1.5 text-xs">
              <span className="shrink-0 font-medium text-gray-500">
                {toTitleCase(key)}:
              </span>
              <span className="text-gray-700">
                {typeof item === "object" ? renderNestedValue(item) : formatValue(item, key)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return String(value);
  };

  const renderFieldValue = (df: DisplayField): React.ReactNode =>
    shouldRenderNestedValue(df.value, df.field)
      ? renderNestedValue(df.value)
      : formatFieldValue(df.field, df.value);

  const buildStepDisplay = (
    step: ViewStep,
    data: JsonObject,
  ): StepDisplay => {
    const fields = getStepFields(step);
    const order = FIELD_ORDER_BY_STEP[step.stepId] || [];
    const rank = (key: string) => {
      const index = order.indexOf(key);
      return index === -1 ? order.length : index;
    };
    const handledKeys = new Set<string>();
    const display: DisplayField[] = [];

    fields.forEach((field) => {
      if (!ID_FIELDS_WITH_NAME_SIBLING.has(field.key)) return;

      const nameKey = field.key.replace(/Id$/, "Name");
      handledKeys.add(field.key);
      handledKeys.add(nameKey);

      if (!isBlank(data[nameKey])) {
        display.push({
          key: nameKey,
          label: field.label || toTitleCase(field.key.replace(/Id$/, "")),
          value: data[nameKey],
          field,
        });
      }
    });

    fields.forEach((field) => {
      if (handledKeys.has(field.key)) return;
      if (!isFieldVisible(field, data)) return;
      if (isBlank(data[field.key])) return;
      if (HIDDEN_UNLESS_TRUE_FIELDS.has(field.key) && data[field.key] !== true) return;

      display.push({
        key: field.key,
        label: field.label || toTitleCase(field.key),
        value: data[field.key],
        field,
      });
    });

    display.sort((a, b) => rank(a.key) - rank(b.key));

    return {
      groups: display.filter((df) => Array.isArray(df.field.fields)),
      narrow: display.filter(
        (df) => !Array.isArray(df.field.fields) && !isWideField(df.field, df.value),
      ),
      wide: display.filter(
        (df) => !Array.isArray(df.field.fields) && isWideField(df.field, df.value),
      ),
    };
  };

  const renderGroupSection = (group: DisplayField) => {
    const groupValue = group.value as JsonObject;
    const subFields = (group.field.fields || []).filter(
      (sub) => !isBlank(groupValue[sub.key]),
    );

    if (!subFields.length) return null;

    return (
      <Section
        key={group.key}
        title={t(`opportunity_view.sections.${group.key}`, {
          defaultValue: tr(group.label),
        })}
      >
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 pt-1">
          {subFields.map((sub) => (
            <InfoPair
              key={sub.key}
              label={getFieldLabel(sub.key, sub.label || toTitleCase(sub.key))}
              value={formatFieldValue(sub, groupValue[sub.key])}
            />
          ))}
        </div>
      </Section>
    );
  };

  const renderStepFields = (step: ViewStep, data: JsonObject) => {
    const { narrow, wide, groups } = buildStepDisplay(step, data);
    const labelOf = (df: DisplayField) =>
      STATIC_LABEL_STEPS.has(step.stepId)
        ? getFieldLabel(df.key, df.label)
        : tr(df.label);

    return (
      <React.Fragment key={step.stepId}>
        {(narrow.length > 0 || wide.length > 0) && (
          <Section title={getSectionTitle(step.title)}>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 pt-1">
              {narrow.map((df) => (
                <InfoPair key={df.key} label={labelOf(df)} value={renderFieldValue(df)} />
              ))}
              {wide.map((df) => (
                <InfoPair
                  key={df.key}
                  wide
                  label={labelOf(df)}
                  value={
                    <div className="whitespace-pre-wrap">{renderFieldValue(df)}</div>
                  }
                />
              ))}
            </div>
          </Section>
        )}
        {groups.map(renderGroupSection)}
      </React.Fragment>
    );
  };

  const renderRepeatableItems = (step: ViewStep, items: JsonObject[]) => {
    const fields = getStepFields(step);

    return (
      <div className="flex flex-col gap-3 pt-1">
        {items.map((item, index) => (
          <div
            key={index}
            className={`rounded-3xl border border-gray-100 bg-white p-4 ${CARD_SHADOW_CLASS}`}
          >
            {fields.length === 0 ? (
              renderNestedValue(item)
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                {fields
                  .filter(
                    (field) =>
                      item[field.key] !== undefined && isFieldVisible(field, item),
                  )
                  .map((field) => (
                    <InfoPair
                      key={field.key}
                      wide={isWideField(field, item[field.key])}
                      label={tr(field.label || toTitleCase(field.key))}
                      value={
                        shouldRenderNestedValue(item[field.key], field)
                          ? renderNestedValue(item[field.key])
                          : formatFieldValue(field, item[field.key])
                      }
                    />
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStep = (step: ViewStep) => {
    const stepData = formData[step.stepId];
    if (!stepData) return null;

    const isPayoutPolicies =
      step.stepId === "policies" ||
      /payout\s*policies/i.test(String(step.title || ""));
    const components: FinancialComponent[] = formData.components ?? [];

    if (isPayoutPolicies) {
      return (
        <Section
          key={step.stepId}
          title={getSectionTitle(step.title)}
          description={t("opportunity_view.descriptions.payoutPolicies")}
        >
          <PayoutPolicyList
            stepData={stepData}
            step={step}
            baseAmount={baseAmount}
            currencySymbol={currencySymbol}
            components={components}
            getOptionLabel={getOptionLabel}
          />
        </Section>
      );
    }

    if (step.stepId === COMPONENTS_STEP_ID) {
      return (
        <Section
          key={step.stepId}
          title={getSectionTitle(step.title)}
          description={t("opportunity_view.descriptions.components")}
        >
          <FinancialComponentList
            items={components}
            baseAmount={baseAmount}
            currencySymbol={currencySymbol}
            getOptionLabel={getOptionLabel}
            getFundingStageLabel={(value) =>
              getOptionLabel(`${COMPONENTS_STEP_ID}.fundingStage`, value)
            }
            showFundingStage={
              String(formData?.core?.paymentMode).toLowerCase() === "split"
            }
          />
        </Section> 
      );
    }

    if (step.repeatable) {
      if (!Array.isArray(stepData) || stepData.length === 0) return null;
      return (
        <Section key={step.stepId} title={getSectionTitle(step.title)}>
          {renderRepeatableItems(step, stepData as JsonObject[])}
        </Section>
      );
    }

    if (step.stepId === NOTES_STEP_ID) {
      return (
        <Section key={step.stepId} title={getSectionTitle(step.title)}>
          <p className="whitespace-pre-wrap text-base font-medium leading-6 text-gray-900">
            {safe((stepData as JsonObject)[getStepFields(step)[0]?.key])}
          </p>
        </Section>
      );
    }

    return renderStepFields(step, stepData as JsonObject);
  };

  const renderContent = () => {
    if (status === "loading") {
      return (
        <div className="flex h-full items-center justify-center">
          <Spinner size="lg" color="primary" label={t("loading")} />
        </div>
      );
    }

    if (!viewCard) {
      return <p className="py-10 text-center text-sm text-gray-400">{t("no_data")}</p>;
    }

    return (
      <div className="flex h-full items-start gap-6 overflow-hidden">
        <div className="max-h-full w-[352px] shrink-0 overflow-y-auto rounded-3xl">
          <ManagerPanel
            manager={opportunity?.manager}
            onProfileOpen={handleProfileNavigation}
          />
        </div>

        <div className="h-full min-w-0 flex-1 overflow-y-auto pr-1 [scrollbar-width:thin]">
          <div className="flex flex-col gap-6 pb-1">
            <ContractProgress templateJson={template} formData={formData} />
            {steps.map(renderStep)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      disableAnimation
      scrollBehavior="inside"
      classNames={{
        wrapper: "overflow-hidden",
        base:
          "w-full max-w-[1133px] h-[776px] max-h-[92vh] p-6 gap-6 rounded-3xl bg-white font-['Plus_Jakarta_Sans'] " +
          "shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08),0px_-6px_12px_0px_rgba(0,0,0,0.03),0px_2px_8px_0px_rgba(0,0,0,0.06)]",
        header: "p-0 flex-col items-start gap-2 pr-6 flex-shrink-0",
        body: "p-0 flex-1 min-h-0 overflow-hidden",
        closeButton:
          "top-4 end-2.5 h-6 w-6 min-w-6 p-1 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 active:bg-gray-200",
      }}
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <span className="text-base font-medium leading-6 text-gray-900">
            {t("opportunity_details")}
          </span>
          <span className="text-sm font-normal leading-5 text-gray-500">
            {t("opportunity_details_subtitle")}
          </span>
        </ModalHeader>

        <ModalBody>{renderContent()}</ModalBody>

        {status === "ready" && viewCard && (
          <RoleBasedActions
            viewCard={viewCard}
            activeTab={activeTab}
            onClose={onClose}
            onRefresh={onRefresh}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

export default OpportunityViewModal;